import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  AdPlatformReportingClient,
  Feature,
  ReportingFlightReport,
  ReportingPerformanceMetrics,
  SponsoredListingsCampaign,
  SponsoredListingsFlight,
} from 'src/WebApiController';

/**
 * Applies reporting metrics to a flight object, returning a new clone with metrics set.
 */
const applyFlightMetrics = (
  flight: SponsoredListingsFlight,
  metrics: ReportingPerformanceMetrics
): SponsoredListingsFlight => {
  const flightWithMetrics: SponsoredListingsFlight = { ...flight };
  flightWithMetrics.historicPotentialImpressions = metrics.eventImpressions;
  flightWithMetrics.actualImpressions = metrics.adImpressions;
  flightWithMetrics.impressionShare =
    (metrics.eventImpressions ?? 0) > 0
      ? (metrics.adImpressions ?? 0) / (metrics.eventImpressions ?? 0)
      : null;
  flightWithMetrics.clicks = metrics.clicks;
  flightWithMetrics.salesCount = metrics.adSalesCount;
  flightWithMetrics.salesProceeds = metrics.adSalesProceeds;
  flightWithMetrics.flightSpend = metrics.adSpendUSD;
  return flightWithMetrics;
};

/**
 * Custom hook to fetch and merge reporting metrics with campaigns data.
 * Fetches both campaign-level and flight-level metrics, and merges them
 * into campaigns and their nested flight objects.
 *
 * @param campaigns - Array of campaigns to enrich with reporting metrics
 * @returns Campaigns array enriched with reporting metrics (or original campaigns if metrics not available)
 */
export const useCampaignsTableMetricsData = (
  campaigns: SponsoredListingsCampaign[] | null | undefined
): SponsoredListingsCampaign[] | null | undefined => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const hasReportingMetricsFeature = useUserHasFeature(
    Feature.SponsoredListingsV2_ReportingMetrics
  );

  // Extract campaign IDs for metrics query
  const campaignIds = useMemo(
    () => (campaigns ?? []).map((c) => c.campaignId),
    [campaigns]
  );

  // Build campaign-flights request for flight metrics query
  const campaignFlights = useMemo(
    () =>
      (campaigns ?? []).map((c) => ({
        campaignId: c.campaignId,
        flightIds: [
          ...(c.activeFlight ? [c.activeFlight.flightId] : []),
          ...(c.flights?.map((f) => f.flightId) ?? []),
        ],
      })),
    [campaigns]
  );

  // Determine date range for metrics query
  // Use a very early date for start (to capture all historical data) and current date for end
  const metricsDateRange = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date('2020-01-01'); // Use a fixed early date to capture all historical metrics

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  }, []);

  // Query to fetch campaign metrics and flight metrics
  const shouldQueryMetrics = useMemo(
    () =>
      hasReportingMetricsFeature &&
      activeAccountWebClientConfig.activeAccountId != null &&
      campaignIds.length > 0,
    [
      hasReportingMetricsFeature,
      activeAccountWebClientConfig.activeAccountId,
      campaignIds.length,
    ]
  );

  // Query key for campaign metrics
  const campaignMetricsQueryKey = useMemo(
    () => [
      'AdPlatformReportingClient.getCampaignMetricsByIds',
      activeAccountWebClientConfig.activeAccountId,
      campaignIds,
      metricsDateRange.startDate,
      metricsDateRange.endDate,
    ],
    [
      activeAccountWebClientConfig.activeAccountId,
      campaignIds,
      metricsDateRange.startDate,
      metricsDateRange.endDate,
    ]
  );

  // Query to fetch campaign metrics
  const campaignMetricsQuery = useQuery({
    queryKey: campaignMetricsQueryKey,
    queryFn: async () => {
      if (!shouldQueryMetrics) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const client = new AdPlatformReportingClient(
            activeAccountWebClientConfig
          );
          const result = await client.getCampaignMetricsByIds({
            campaignIds: campaignIds,
            startDate: metricsDateRange.startDate,
            endDate: metricsDateRange.endDate,
          });
          return result;
        },
        (error) => {
          trackError(
            'AdPlatformReportingClient.getCampaignMetricsByIds',
            error,
            {
              campaignIds,
              startDate: metricsDateRange.startDate,
              endDate: metricsDateRange.endDate,
            }
          );
        }
      );
    },
    enabled: shouldQueryMetrics,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  // Query key for flight metrics
  const flightMetricsQueryKey = useMemo(
    () => [
      'AdPlatformReportingClient.getFlightsMetricsByCampaignIds',
      activeAccountWebClientConfig.activeAccountId,
      campaignFlights,
      metricsDateRange.startDate,
      metricsDateRange.endDate,
    ],
    [
      activeAccountWebClientConfig.activeAccountId,
      campaignFlights,
      metricsDateRange.startDate,
      metricsDateRange.endDate,
    ]
  );

  // Query to fetch flight metrics
  const flightMetricsQuery = useQuery({
    queryKey: flightMetricsQueryKey,
    queryFn: async () => {
      if (!shouldQueryMetrics) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const client = new AdPlatformReportingClient(
            activeAccountWebClientConfig
          );
          const result = await client.getFlightsMetricsByCampaignIds({
            campaignFlights: campaignFlights,
            startDate: metricsDateRange.startDate,
            endDate: metricsDateRange.endDate,
          });
          return result;
        },
        (error) => {
          trackError(
            'AdPlatformReportingClient.getFlightsMetricsByCampaignIds',
            error,
            {
              campaignFlights,
              startDate: metricsDateRange.startDate,
              endDate: metricsDateRange.endDate,
            }
          );
        }
      );
    },
    enabled: shouldQueryMetrics,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  // Merge campaign and flight metrics into campaigns data
  const campaignsWithMetrics = useMemo(() => {
    if (
      !campaigns ||
      (!campaignMetricsQuery.data?.campaigns &&
        !flightMetricsQuery.data?.flights)
    ) {
      return campaigns;
    }

    // Build campaign metrics map
    const campaignMetricsMap = new Map(
      (campaignMetricsQuery.data?.campaigns ?? []).map((report) => [
        report.campaignId,
        report,
      ])
    );

    // Build flight metrics map (flightId -> report)
    const flightMetricsMap = new Map<number, ReportingFlightReport>(
      (flightMetricsQuery.data?.flights ?? []).map((report) => [
        report.flightId!,
        report,
      ])
    );

    return campaigns.map((campaign) => {
      const campaignReport = campaignMetricsMap.get(campaign.campaignId);

      // Clone the campaign
      const campaignWithMetrics: SponsoredListingsCampaign = { ...campaign };

      // Apply campaign-level metrics
      if (campaignReport?.totalMetrics) {
        campaignWithMetrics.historicPotentialImpressions =
          campaignReport.totalMetrics.eventImpressions;
        campaignWithMetrics.actualImpressions =
          campaignReport.totalMetrics.adImpressions;
        campaignWithMetrics.impressionShare =
          (campaignReport.totalMetrics.eventImpressions ?? 0) > 0
            ? (campaignReport.totalMetrics.adImpressions ?? 0) /
              (campaignReport.totalMetrics.eventImpressions ?? 0)
            : null;
        campaignWithMetrics.clicks = campaignReport.totalMetrics.clicks;
        campaignWithMetrics.salesCount =
          campaignReport.totalMetrics.adSalesCount;
        campaignWithMetrics.salesProceeds =
          campaignReport.totalMetrics.adSalesProceeds;
        campaignWithMetrics.totalSpend = campaignReport.totalMetrics.adSpendUSD;
      }

      // Apply flight-level metrics to activeFlight
      if (campaignWithMetrics.activeFlight) {
        const flightReport = flightMetricsMap.get(
          campaignWithMetrics.activeFlight.flightId
        );
        if (flightReport?.metrics) {
          campaignWithMetrics.activeFlight = applyFlightMetrics(
            campaignWithMetrics.activeFlight,
            flightReport.metrics
          );
        }
      }

      // Apply flight-level metrics to flights array
      if (campaignWithMetrics.flights) {
        campaignWithMetrics.flights = campaignWithMetrics.flights.map(
          (flight) => {
            const flightReport = flightMetricsMap.get(flight.flightId);
            if (flightReport?.metrics) {
              return applyFlightMetrics(flight, flightReport.metrics);
            }
            return flight;
          }
        );
      }

      return campaignWithMetrics;
    });
  }, [campaigns, campaignMetricsQuery.data, flightMetricsQuery.data]);

  return campaignsWithMetrics;
};
